import { observable, action } from 'mobx';
import { IPeopleFinderPerson } from '../../../interfaces/IPeopleFinderPerson';
import { ITenantSettings } from '../../../interfaces/ITenantSettings';
import { IPeopleFinderDesign } from '../../../interfaces/IPeopleFinderDesign';
import { IPeopleFinderFilter, IFilterProperty } from '../../../interfaces/IPeopleFinderFilter';
import { PeopleFinderService } from '../../../services/PeopleFinderService';
import { ConditionalOutput } from '../../../services/ConditionalOutput';

export enum DisplayedPeopleType {
  SUGGESTED,
  SEARCED,
}

export class PeopleFinderStore {
  private peopleFinderService = new PeopleFinderService();

  // settings
  @observable public width: number;
  @observable public height: number;
  @observable public contentHeight: number;
  @observable public headerHeight: number;
  @observable public type: 'navigator' | 'webpart';

  // design
  @observable public design: IPeopleFinderDesign;

  // config
  @observable public environment: 'Development' | 'Test' | 'Production';
  @observable public token: string;
  @observable public settings: ITenantSettings;
  @observable public tenant: string;
  @observable public tenantName: string;

  // panel
  @observable public isPanelOpen: boolean;
  @observable public isFecthingSkillsAndDescription: boolean;
  @observable public selectedPerson: IPeopleFinderPerson;

  // Cards
  @observable public displayedCardType: DisplayedPeopleType;
  @observable public people: IPeopleFinderPerson[];

  // Paging
  @observable public showPaging: boolean;
  @observable public isFetchingNextPage: boolean;
  @observable public nextPageToken: string;
  @observable public pagingIndex: number;
  @observable public cardsAmount: number;
  @observable public isOnLastPage: boolean;
  @observable public showRightPaging: boolean;
  @observable public showLeftPaging: boolean;


  // suggested people
  @observable public suggestedPeople: IPeopleFinderPerson[];
  @observable public isFetchingSuggestedPeople: boolean;

  // my profile
  @observable public profile: IPeopleFinderPerson;
  @observable public isFetchingProfile: boolean;

  // Search
  @observable public searchValue: string = '';
  @observable public searchedResults: IPeopleFinderPerson[];
  @observable public showResultList: boolean;
  @observable public isFetchingSearchResults: boolean;
  @observable public showMore: boolean;

  // Filter
  private selectedFilters: { departments: string[]; locations: string[]; jobTitles: string[] };
  @observable public filter: IPeopleFinderFilter;
  @observable public showFilter: boolean;
  @observable public isLoadingFilter: boolean;

  @action public closePanel = () => {
    this.isPanelOpen = false;
    this.selectedPerson = undefined;
  }

  @action public openPanel = (person: IPeopleFinderPerson) => {
    this.selectedPerson = { ...person };
    this.isPanelOpen = true;
    this.getSkillsAndDescription();
  }

  @action public setFilter(filter: IPeopleFinderFilter) {
    this.filter = filter;
  }

  @action public isFilterSelected() {
    const newSelectJobTitleLength = this.filter?.jobTitles?.filter((v) => v.selected).length;
    const newSelectDepartmentsLength = this.filter?.departments?.filter((v) => v.selected).length;
    return newSelectDepartmentsLength > 0 || newSelectJobTitleLength > 0;
  }

  @action public toggleFilter(type: 'locations' | 'departments' | 'jobTitles', options: IFilterProperty[]) {
    if (!!!this.selectedFilters) {
      this.selectedFilters = {
        departments: [],
        jobTitles: [],
        locations: [],
      };
    }
    this.filter[type] = options;
    this.getSearched();
  }

  @action public resize(_width: number): void {
    this.cardsAmount = 16;
    this.pagingIndex = 0;
  }

  @action public async nextPage() {
    const nextPagingIndex = this.pagingIndex + 1;
    const hasNextPage = nextPagingIndex !== Math.round((this.people.length) / this.cardsAmount);
    const mustFetchNextPage = !hasNextPage && !!this.nextPageToken;
    if (hasNextPage && !mustFetchNextPage) {
      this.pagingIndex = nextPagingIndex;
      this.showLeftPaging = true;
      const calc = this.people.length / (this.cardsAmount + 1);
      const pagesAmount = Math.round(calc);
      this.showRightPaging = pagesAmount !== nextPagingIndex;
    }
    if (mustFetchNextPage) {
      this.isFetchingNextPage = true;
      this.getSearched(true).then(() => {
        this.pagingIndex = nextPagingIndex;
        this.showLeftPaging = true;
        this.isFetchingNextPage = false;
        this.showRightPaging = this.nextPageToken !== undefined;
      });
    }
  }

  @action public prevPage() {
    this.pagingIndex = this.pagingIndex > 0 ? this.pagingIndex - 1 : 0;
    const isLastIndex = this.pagingIndex + 1 === Math.round((this.people.length) / this.cardsAmount);
    if (!isLastIndex) {
      this.showRightPaging = true;
    }
    this.showLeftPaging = this.pagingIndex > 0;
  }

  @action public async getSkillsAndDescription() {
    this.isFecthingSkillsAndDescription = true;
    const skillsAndDescription = await this.peopleFinderService.getAdditionalUserInformation(this.tenantName, this.token, this.environment, this.selectedPerson.id);
    if (!!skillsAndDescription) {
      this.selectedPerson = { ...this.selectedPerson, ...skillsAndDescription };
      this.isFecthingSkillsAndDescription = false;
    }
  }

  @action public async showSuggestedResult() {
    this.displayedCardType = DisplayedPeopleType.SUGGESTED;
    if (this.suggestedPeople?.length > 0 && !this.showMore) {
      this.people = this.suggestedPeople;
    } else {
      this.getSuggestedPeople();
    }
  }

  @action public async getFilterLists() {
    const filter = await this.peopleFinderService.getFilteringLists(this.tenantName, this.token, this.environment);
    const options = {
      departments: filter?.departments.map((v) => {
        return {
          selected: false,
          text: v,
          key: v,
        };
      }),
      locations: filter?.locations.map((v) => {
        return {
          selected: false,
          text: v,
          key: v,
        };
      }),
      jobTitles: filter?.jobTitles.map((v) => {
        return {
          selected: false,
          text: v,
          key: v,
        };
      }),
    };
    this.filter = options;
  }

  @action public async getSuggestedPeople() {
    this.isFetchingSuggestedPeople = true;
    const people = await this.peopleFinderService.getSuggestedPeople(this.tenantName, this.cardsAmount, this.profile.id, this.token, this.environment);
    if (!!people) {
      this.suggestedPeople = people.result;
      this.people = people.result;
      this.isFetchingSuggestedPeople = false;
    }
  }

  private getFilterQuery() {
    const stringQuery: string[] = [];
    const selectedDepartments = this.filter?.departments?.filter((v) => v.selected).map((v) => v.key);
    // const selectedLocations = this.filter?.locations?.filter((v) => v.selected).map((v) => v.key);
    const selectedJobTiltes = this.filter?.jobTitles?.filter((v) => v.selected).map((v) => v.key);
    if (!!this.searchValue) {
      stringQuery.push(`startsWith(displayName, '${this.searchValue}')`);
    }
    if (selectedDepartments?.length > 0) {
      stringQuery.push(selectedDepartments.map((department) => `department eq '${department}'`).join(' OR '));
    }
    if (selectedJobTiltes?.length > 0) {
      stringQuery.push(selectedJobTiltes.map((jobTitle) => `jobTitle eq '${jobTitle}'`).join(' OR '));
    }
    return stringQuery.join(' and ');
  }

  @action public async getSearched(isNextPage?: boolean) {
    if (!isNextPage) { this.isFetchingSearchResults = true; }
    const filterQuery = this.getFilterQuery();
    const searchValue = this.searchValue;
    const response = await this.peopleFinderService.getAllPeople(this.tenantName, this.token, this.environment, filterQuery, isNextPage ? this.nextPageToken : undefined, this.cardsAmount);
    const isLatestFetch = this.searchValue === searchValue && this.getFilterQuery() === filterQuery;
    const isValidAndLatest = !!response && isLatestFetch;
    const isDisplayTypeSearch = this.displayedCardType === DisplayedPeopleType.SEARCED;
    const hasManyPages = this.cardsAmount < this.people.length;
    this.searchedResults = response.result;
    if (isValidAndLatest) {
      this.nextPageToken = !!response.skipReference ? response.skipReference : undefined;
      this.showPaging = !!this.nextPageToken || hasManyPages;
      this.showRightPaging = !!this.nextPageToken;
      this.pagingIndex = isNextPage ? this.pagingIndex : 0;
      this.people = new ConditionalOutput<IPeopleFinderPerson[]>(this.suggestedPeople)
        .OnCondition(isDisplayTypeSearch, response.result)
        .OnCondition(isNextPage && isDisplayTypeSearch, this.people.concat(response.result))
        .Last();
    }
    this.isFetchingSearchResults = false;
  }

  @action public showResultsInFullView() {
    this.displayedCardType = DisplayedPeopleType.SEARCED;
    this.people = this.searchedResults;
    this.showResultList = false;
    this.showMore = true;
  }

  @action public async getProfileData() {
    this.isFetchingProfile = true;
    const profile = await this.peopleFinderService.getProfile(this.tenantName, this.token, this.environment);
    if (!!profile.id) {
      this.profile = profile;
      this.isFetchingProfile = false;
    }
  }
}
